import React from 'react';

type Props = {
  restaurant_name: string;
  profile_image_url?: string;
  bio?: string;
};

// Helper function to check if content is HTML or plain text
const isHtmlContent = (content: string): boolean => {
  // Check for common HTML tags that would indicate rich text content
  const htmlTagRegex = /<(p|h[1-6]|ul|ol|li|strong|em|u|s|a|img|br)\b[^>]*>/i;
  return htmlTagRegex.test(content);
};

// Helper function to convert plain text to HTML for rendering
const convertPlainTextToHtml = (plainText: string): string => {
  if (!plainText || plainText.trim() === '') {
    return '';
  }
  
  // Split by double line breaks to create paragraphs
  const paragraphs = plainText.split(/\n\s*\n/);
  
  return paragraphs
    .map(paragraph => {
      if (paragraph.trim() === '') return '';
      
      // Convert single line breaks to <br> tags within paragraphs
      const formattedParagraph = paragraph
        .trim()
        .replace(/\n/g, '<br>');
      
      return `<p class="text-on-surface-variant">${formattedParagraph}</p>`;
    })
    .filter(p => p !== '')
    .join('');
};

// Helper function to add text classes to HTML elements
const addTextClassesToHtml = (html: string): string => {
  if (!html || html.trim() === '') {
    return '';
  }
  
  // Add text-on-surface-variant class to various text elements
  let processedHtml = html;
  
  // Process different HTML elements and add the text class
  const textElements = [
    'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'del', 'ins', 'sub', 'sup',
    'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'span', 'div'
  ];
  
  textElements.forEach(element => {
    // Handle elements that already have classes
    processedHtml = processedHtml.replace(
      new RegExp(`<${element}\\s+class="([^"]*)"`, 'gi'),
      `<${element} class="$1 text-on-surface-variant"`
    );
    
    // Handle elements that already have other attributes but no class
    processedHtml = processedHtml.replace(
      new RegExp(`<${element}\\s+([^>]*?)(?<!class="[^"]*")>`, 'gi'),
      (match, attrs) => {
        if (attrs.includes('class=')) {
          return match; // Already handled above
        }
        return `<${element} ${attrs} class="text-on-surface-variant">`;
      }
    );
    
    // Handle elements with no attributes
    processedHtml = processedHtml.replace(
      new RegExp(`<${element}>`, 'gi'),
      `<${element} class="text-on-surface-variant">`
    );
  });
  
  return processedHtml;
};

// Helper function to prepare content for rendering
const prepareContentForRendering = (content: string): string => {
  if (!content || content.trim() === '') {
    return '';
  }
  
  let processedContent: string;
  
  // If it's already HTML, use as-is
  if (isHtmlContent(content)) {
    processedContent = content;
  } else {
    // If it's plain text, convert to HTML
    processedContent = convertPlainTextToHtml(content);
  }
  
  // Add text classes to HTML elements
  return addTextClassesToHtml(processedContent);
};

const RestaurantNameCard: React.FC<Props> = ({
  restaurant_name,
  profile_image_url,
  bio,
}) => {
  // Function to safely render bio content (both HTML and plain text)
  const renderBioContent = (bioContent: string) => {
    const preparedContent = prepareContentForRendering(bioContent);
    // Remove empty paragraphs that might be created
    const cleanedHTML = preparedContent.replace(/<p><\/p>/g, '');
    return { __html: cleanedHTML };
  };

  return (
    <div className="align-bottom bg-surface rounded-lg text-left overflow-hidden shadow-md transform transition-all sm:my-8 sm:align-middle mx-4 mt-4">
      <div className="flex flex-col md:flex-row md:items-stretch bg-primary/10 p-4 md:p-0">
        {profile_image_url && (
          <div className="md:shrink-0 md:self-stretch">
            <img
              className="mx-auto md:mx-0 h-32 w-32 md:h-full md:w-52 flex-shrink-0 object-cover rounded-md md:rounded-none"
              src={profile_image_url}
              alt="profile image"
            />
          </div>
        )}
        <div className="mt-2 md:mt-0 flex flex-col justify-start md:p-4 flex-1 min-w-0 w-full">
          <div className="flex items-start justify-start text-lg text-on-surface-variant font-bold w-full">
            {restaurant_name}
          </div>
          {bio && bio?.length !== 0 && (
            <div 
              className="text-md text-on-surface-variant prose prose-sm !max-w-full w-full"
              dangerouslySetInnerHTML={renderBioContent(bio)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantNameCard; 